import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Btn } from "@/components/kit";
import { usd } from "@/lib/format";
import { Users, X, Loader2, Trophy, DoorClosed } from "lucide-react";
import { toast } from "sonner";

const KNOCK = [["not_knocked", "Not knocked"], ["no_answer", "No answer"], ["callback", "Callback"], ["not_interested", "Not interested"], ["appointment", "Appointment set"]];
const fmtDay = (d) => { try { return new Date(d + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }); } catch (e) { return d; } };

export default function StormCampaigns({ refreshKey }) {
  const [groups, setGroups] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState(null);
  const [leads, setLeads] = useState([]);
  const [repSel, setRepSel] = useState("");

  const load = () => { setLoading(true); api.get("/hailtrace/storm-groups").then((r) => setGroups(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);
  useEffect(() => { api.get("/auth/users").then((r) => setReps(r.data || [])).catch(() => {}); }, []);

  const repName = (id) => { const u = reps.find((r) => r.id === id); return u ? (u.name || u.email) : "—"; };
  const openManage = async (tag) => { setManage(tag); setRepSel(""); try { const { data } = await api.get(`/hailtrace/storm-leads?tag=${encodeURIComponent(tag)}`); setLeads(data); } catch { setLeads([]); } };
  const assignAll = async () => {
    if (!repSel) return toast.error("Pick a rep to assign");
    try { const { data } = await api.post("/hailtrace/assign", { tag: manage, rep: repSel }); toast.success(`Assigned ${data.updated} leads to ${repName(repSel)}`); openManage(manage); load(); }
    catch { toast.error("Assign failed"); }
  };
  const setKnock = async (lid, ks) => {
    try { await api.put(`/leads/${lid}`, { knock_status: ks }); setLeads((ls) => ls.map((l) => (l.id === lid ? { ...l, knock_status: ks } : l))); load(); }
    catch { toast.error("Update failed"); }
  };

  if (loading) return <div className="mb-card p-5 mt-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading storm campaigns…</div>;
  if (!groups.length) return null;

  return (
    <div className="mb-card p-5 mt-6" data-testid="storm-campaigns">
      <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-brandred" /><h3 className="font-display font-bold uppercase text-lg text-navy">Storm Campaigns & Scorecard</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[720px]">
          <thead>
            <tr className="text-[10px] uppercase text-slate-400 text-left">
              <th className="p-2">Storm</th><th className="p-2">Leads</th><th className="p-2">Knocked</th><th className="p-2">Appts</th><th className="p-2">Proposals</th><th className="p-2">Signed</th><th className="p-2">Revenue</th><th className="p-2">Rep(s)</th><th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.tag} className="border-t border-slate-100" data-testid={`campaign-${g.tag}`}>
                <td className="p-2 font-semibold text-navy whitespace-nowrap">{fmtDay(g.date)}</td>
                <td className="p-2 font-num">{g.leads_saved}</td>
                <td className="p-2 font-num">{g.knocked}</td>
                <td className="p-2 font-num">{g.appointments}</td>
                <td className="p-2 font-num">{g.proposals}</td>
                <td className="p-2 font-num text-navy font-bold">{g.signed}</td>
                <td className="p-2 font-num font-bold text-successg">{usd(g.revenue_cents)}</td>
                <td className="p-2 text-xs text-slate-500 max-w-[140px] truncate">{g.reps.length ? g.reps.join(", ") : "Unassigned"}</td>
                <td className="p-2"><button data-testid={`manage-${g.tag}`} onClick={() => openManage(g.tag)} className="text-xs font-bold text-navy border-2 border-slate-200 hover:border-navy rounded-lg px-2 py-1">Manage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setManage(null)}>
          <div className="mb-card p-6 w-full max-w-2xl animate-scale-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="manage-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl font-bold uppercase text-navy">Storm · {fmtDay(manage.replace("storm_", ""))}</h3>
              <button onClick={() => setManage(null)} data-testid="manage-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="flex items-end gap-2 mb-4 border-2 border-slate-100 rounded-xl p-3">
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Bulk-assign all {leads.length} leads to</label>
                <select data-testid="assign-rep-select" value={repSel} onChange={(e) => setRepSel(e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
                  <option value="">— pick a rep —</option>
                  {reps.map((r) => <option key={r.id} value={r.id}>{r.name || r.email} ({r.role})</option>)}
                </select>
              </div>
              <Btn onClick={assignAll} testid="assign-apply"><Users className="w-4 h-4" /> Assign All</Btn>
            </div>
            <div className="space-y-1.5">
              {leads.map((l) => (
                <div key={l.id} className="flex items-center gap-2 border-b border-slate-100 py-2" data-testid={`lead-row-${l.id}`}>
                  <DoorClosed className="w-4 h-4 text-slate-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{l.contact_name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{l.address} · rep: {l.assigned_rep ? repName(l.assigned_rep) : "unassigned"}</div>
                  </div>
                  <select data-testid={`knock-${l.id}`} value={l.knock_status || "not_knocked"} onChange={(e) => setKnock(l.id, e.target.value)}
                    className="border-2 border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-navy shrink-0">
                    {KNOCK.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
